import { FC, MouseEvent, useEffect, useRef, useState } from 'react';
import { Box, Menu, styled } from '@mui/material';
import { Column } from '../Layout';
import { useScrollSpy } from './useScrollSpy';
import VerticalTabsMenu from './VerticalTabsMenu';
import NestedTableOfContentsMenu, {
  NestedTableOfContentsMenuEntry,
} from './NestedTableOfContentsMenu';

const Rectangle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ active, theme }) => ({
  height: '2px',
  width: '16px',
  backgroundColor: active ? theme.palette.grey[900] : theme.palette.grey[200],
  borderRadius: '100px',
  transition: 'all 150ms ease',
}));

const MenuContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.xs),
}));

const StepperTrigger = styled(Box)({
  display: 'inline-flex',
  cursor: 'pointer',
});

const HoverContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMenuOpen',
})<{ isMenuOpen?: boolean }>(({ theme, isMenuOpen }) => ({
  position: 'relative',
  display: 'inline-flex',
  ...(isMenuOpen && {
    zIndex: theme.zIndex.modal,
  }),
}));

const StyledMenu = styled(Menu)({
  pointerEvents: 'none',
  '& .MuiPaper-root': {
    pointerEvents: 'auto',
  },
});

type FlatSection = {
  id: string;
  label: string;
};

type Props = {
  sections?: Array<FlatSection>;
  entries?: Array<NestedTableOfContentsMenuEntry>;
  intersectionRootMargin?: string;
  onEntrySelect?: (id: string) => void;
  getTopLevelActiveId?: (activeSectionId: string | null) => string | null;
};

const ScrollStepper: FC<Props> = ({
  sections,
  entries,
  intersectionRootMargin,
  onEntrySelect,
  getTopLevelActiveId,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [pendingSectionId, setPendingSectionId] = useState<string | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const menuPaperRef = useRef<HTMLDivElement | null>(null);
  const open = Boolean(anchorEl);

  const flatSectionIds = entries
    ? entries.flatMap((entry) => [
        entry.id,
        ...(entry.subEntries?.map((subEntry) => subEntry.id) ?? []),
      ])
    : sections?.map((section) => section.id) ?? [];

  const topLevelSectionIds = entries
    ? entries.map((entry) => entry.id)
    : sections?.map((section) => section.id) ?? [];

  const spyActiveId = useScrollSpy(flatSectionIds, {
    rootMargin: intersectionRootMargin,
  });
  const activeId = pendingSectionId ?? spyActiveId;
  const activeTopLevelId = getTopLevelActiveId
    ? getTopLevelActiveId(activeId)
    : activeId;

  useEffect(() => {
    if (pendingSectionId && spyActiveId === pendingSectionId) {
      setPendingSectionId(null);
    }
  }, [pendingSectionId, spyActiveId]);

  const scrollToSection = (id: string) => {
    if (onEntrySelect) {
      onEntrySelect(id);
      return;
    }

    const el = document.getElementById(id);
    if (!el) return;

    el.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const handleOpen = () => {
    if (anchorRef.current) {
      setAnchorEl(anchorRef.current);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePointerLeave = (event: MouseEvent<HTMLElement>) => {
    const relatedTarget = event.relatedTarget;

    if (!(relatedTarget instanceof Node)) {
      handleClose();
      return;
    }

    if (
      triggerRef.current?.contains(relatedTarget) ||
      menuPaperRef.current?.contains(relatedTarget)
    ) {
      return;
    }

    handleClose();
  };

  const handleEntrySelect = (id: string) => {
    setPendingSectionId(id);
    scrollToSection(id);
  };

  return (
    <HoverContainer isMenuOpen={open}>
      <StepperTrigger
        ref={triggerRef}
        onMouseEnter={handleOpen}
        onMouseLeave={handlePointerLeave}
      >
        <Column spacing="sm">
          {topLevelSectionIds.map((sectionId, index) => {
            const active = sectionId === activeTopLevelId;

            if (index === 0) {
              return (
                <Box ref={anchorRef} key={sectionId}>
                  <Rectangle active={active} />
                </Box>
              );
            }

            return <Rectangle active={active} key={sectionId} />;
          })}
        </Column>
      </StepperTrigger>
      <StyledMenu
        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorEl={anchorEl}
        open={open}
        hideBackdrop
        onClose={handleClose}
        slotProps={{
          paper: {
            ref: menuPaperRef,
            elevation: 0,
            onMouseEnter: handleOpen,
            onMouseLeave: handlePointerLeave,
          },
        }}
      >
        <MenuContainer>
          {entries ? (
            <NestedTableOfContentsMenu
              entries={entries}
              selectedId={activeId ?? ''}
              onEntrySelect={handleEntrySelect}
            />
          ) : (
            <VerticalTabsMenu
              onTabChange={handleEntrySelect}
              tabs={sections ?? []}
              selectedTab={activeId ?? ''}
            />
          )}
        </MenuContainer>
      </StyledMenu>
    </HoverContainer>
  );
};

export default ScrollStepper;
